// Source de verite FRONTEND des profils d'activite.
// Miroir de services/companyActivityProfiles.js (backend) : tenir synchronise.
//
// CUMUL : une entreprise peut avoir PLUSIEURS profils
// (companies.activity_profiles, JSON). `activity_profile` reste le principal.
// Toute lecture cote UI passe par getCompanyProfiles / companyHasProfile :
// semantique "AU MOINS un profil de l'entreprise correspond".

export interface ActivityProfile {
  key: string;
  label: string;
  domain: string; // business_domain (les profils sont mono-domaine)
}

export const ACTIVITY_PROFILES: ActivityProfile[] = [
  { key: "restaurant", label: "Restauration", domain: "commercial" },
  { key: "bakery", label: "Boulangerie", domain: "commercial" },
  { key: "fast_food", label: "Fast-food", domain: "commercial" },
  { key: "retail", label: "Commerce de detail", domain: "commercial" },
  {
    key: "production",
    label: "Production / transformation",
    domain: "commercial",
  },
  { key: "ecommerce", label: "E-commerce", domain: "commercial" },
  { key: "commercial_services", label: "Services commerciaux", domain: "commercial" },
  {
    key: "freight_forwarding",
    label: "Logistique & Freight forwarding",
    domain: "logistique",
  },
  { key: "hotel", label: "Hotel / hebergement", domain: "hotellerie" },
  { key: "hr_services", label: "Services RH", domain: "rh" },
];

const PROFILE_MAP: Record<string, ActivityProfile> = ACTIVITY_PROFILES.reduce(
  (acc, profile) => {
    acc[profile.key] = profile;
    return acc;
  },
  {} as Record<string, ActivityProfile>
);

// Options antd (Select) derivees de la source unique.
export const ACTIVITY_PROFILE_OPTIONS = ACTIVITY_PROFILES.map((profile) => ({
  value: profile.key,
  label: profile.label,
  domain: profile.domain,
}));

const normalizeKey = (value: unknown): string =>
  String(value === undefined || value === null ? "" : value)
    .trim()
    .toLowerCase();

// array | JSON string | CSV string | scalaire -> cles VALIDES, dedupliquees.
export const normalizeActivityProfiles = (value: unknown): string[] => {
  if (value === undefined || value === null) return [];
  let list: any = value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        list = JSON.parse(trimmed);
      } catch {
        list = trimmed.split(",");
      }
    } else {
      list = trimmed.split(",");
    }
  }
  if (!Array.isArray(list)) list = [list];
  const cleaned = list
    .map((item: unknown) => normalizeKey(item))
    .filter((item: string) => item && PROFILE_MAP[item]);
  return Array.from(new Set<string>(cleaned));
};

// Source unique : accepte un user ({ company }), une company, un array ou un
// scalaire. Fallback sur le profil principal si l'ensemble cumule est absent
// (colonne non migree / payload ancien).
export const getCompanyProfiles = (input: any): string[] => {
  if (Array.isArray(input) || typeof input === "string") {
    return normalizeActivityProfiles(input);
  }
  if (!input || typeof input !== "object") return [];
  const company =
    input.company && typeof input.company === "object" ? input.company : input;
  const multi = normalizeActivityProfiles(company.activity_profiles);
  if (multi.length) return multi;
  const primary = normalizeKey(company.activity_profile);
  return primary && PROFILE_MAP[primary] ? [primary] : [];
};

export const companyHasProfile = (input: any, key: string): boolean =>
  getCompanyProfiles(input).includes(normalizeKey(key));

// Resolve activity profiles from the company currently selected in the UI.
// This matters for SysAdmin accounts: their own `user.company` is not
// necessarily the company they are inspecting, while assigned_warehouses
// contains every selectable company.
export const getSelectedCompanyProfiles = (
  user: any,
  selectedCompanyId: unknown
): string[] => {
  const selectedId = Number(selectedCompanyId);
  const hasSelectedCompany = Number.isFinite(selectedId) && selectedId > 0;

  if (!hasSelectedCompany) {
    return getCompanyProfiles(user);
  }

  const ownCompanyId = Number(user?.company?.id ?? user?.company_id);
  if (Number.isFinite(ownCompanyId) && ownCompanyId === selectedId) {
    const ownProfiles = getCompanyProfiles(user);
    if (ownProfiles.length) return ownProfiles;
  }

  const warehouses = Array.isArray(user?.assigned_warehouses)
    ? user.assigned_warehouses
    : [];
  const selectedCompanyWarehouse = warehouses.find(
    (warehouse: any) => Number(warehouse?.company_id) === selectedId
  );

  if (!selectedCompanyWarehouse) return [];

  return getCompanyProfiles(
    selectedCompanyWarehouse.company || {
      activity_profile:
        selectedCompanyWarehouse.company_activity_profile ??
        selectedCompanyWarehouse.activity_profile,
      activity_profiles:
        selectedCompanyWarehouse.company_activity_profiles ??
        selectedCompanyWarehouse.activity_profiles,
    }
  );
};

export const selectedCompanyHasProfile = (
  user: any,
  selectedCompanyId: unknown,
  key: string
): boolean =>
  getSelectedCompanyProfiles(user, selectedCompanyId).includes(
    normalizeKey(key)
  );

export const companyHasAnyProfile = (input: any, keys: string[]): boolean => {
  const profiles = getCompanyProfiles(input);
  return keys.some((key) => profiles.includes(normalizeKey(key)));
};

export const getActivityProfileLabel = (value: unknown): string | null => {
  const key = normalizeKey(value);
  return key && PROFILE_MAP[key] ? PROFILE_MAP[key].label : null;
};

export const getActivityProfileLabels = (input: any): string[] =>
  getCompanyProfiles(input)
    .map((key) => PROFILE_MAP[key] && PROFILE_MAP[key].label)
    .filter((label): label is string => Boolean(label));

// Domaines metier couverts par un ensemble de profils (union).
export const getProfileDomains = (input: any): string[] => {
  const domains = new Set<string>();
  getCompanyProfiles(input).forEach((key) => {
    const domain = PROFILE_MAP[key] && PROFILE_MAP[key].domain;
    if (domain) domains.add(domain);
  });
  return Array.from(domains);
};

// Domaine impose par une selection de profils (contrainte cumul INTRA-domaine).
// Retourne le domaine du 1er profil valide, ou null si la selection est vide.
export const getSelectionDomain = (keys: string[]): string | null => {
  for (const key of keys) {
    const profile = PROFILE_MAP[normalizeKey(key)];
    if (profile) return profile.domain;
  }
  return null;
};

// Vrai si tous les profils partagent au plus un domaine (<= 1 profil -> true).
export const profilesShareDomain = (keys: string[]): boolean => {
  const domains = new Set(
    keys
      .map((key) => PROFILE_MAP[normalizeKey(key)] && PROFILE_MAP[normalizeKey(key)].domain)
      .filter(Boolean)
  );
  return domains.size <= 1;
};
